import React, { Fragment } from "react";
import { Card, CardBody, Row, Col, Progress, CardHeader, Table, Badge } from "reactstrap";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";
import { LoadingMessage, GetRiskClassName } from "./helper";
import style from "./styles.module.scss";
import { useState, useEffect } from "react";

export const AccountRiskTable = ({ account, riskSummary, decimalPlaces, services }) => {
  const [isRiskByPerson, setIsRiskByPerson] = useState(false);

  // Use useEffect to handle the service call
  useEffect(() => {
    services.getParameterValue({ ParameterName: "RiskByPerson" }, (response) => {
      if (response && response.SdtParametersData && response.SdtParametersData.ParameterNumericValue == 1) {
        setIsRiskByPerson(true);
      }
    });
  }, [services]);

  return (
  <Row className={style.risk}>
    <Col lg="12">
      {!account ? (
        <LoadingMessage text="Loading account info" />
      ) : (
        <Fragment>
          <Card className="mt-1 mb-3">
            <CardHeader className="pt-2 pb-2">Risk</CardHeader>
            <CardBody>
              <Row>
                <Col lg="9">
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Factor description</th>
                        <th className="text-right">Factor Weight</th>
                        <th>Data</th>
                        <th className="text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <LoadingOrEmpty
                        isLoading={!riskSummary}
                        isEmpty={riskSummary && riskSummary.Risk.length == 0}
                      >
                        {riskSummary &&
                          riskSummary.Risk.map((risk) => (
                            <tr key={`factor-${risk.FactorId}`}>
                              <td className={style.title}>{risk.FactorDescription}</td>
                              <td className="text-right">{risk.FactorWeight}</td>
                              <td className="text-center" >{risk.Data}</td>
                              <td className="text-right">{Number.parseFloat(risk.Score).toFixed(decimalPlaces)}</td>
                            </tr>
                          ))}
                      </LoadingOrEmpty>
                    </tbody>
                  </Table>
                </Col>
                <Col lg="3">
                  <RiskSummary account={account} riskSummary={riskSummary} />
                </Col>
              </Row>
              {riskSummary && riskSummary.Risk.length != 0 && (
                <Row>
                  <Col sm="9">
                    <div className="pt-3 pl-2 pr-2">
                      <h6>
                        <span className="float-right d-block text-primary ">
                          Total {getTotalRiskScore(riskSummary, decimalPlaces)} points
                        </span>
                      </h6>
                    </div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader className="pt-2 pb-2">Profile Risk</CardHeader>
            <CardBody>
              <Row>
                <Col lg="9">
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Answer</th>
                        <th className="text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <LoadingOrEmpty
                        isLoading={!riskSummary}
                        isEmpty={riskSummary && riskSummary.ProfileRisk.length == 0}
                      >
                        {riskSummary &&
                          riskSummary.ProfileRisk.map((risk) => (
                            <tr key={`factor-${risk.QuestionId}`}>
                              <td className={style.title}>{risk.QuestionDescription}</td>
                              <td>{risk.AnswerDescription}</td>
                              <td className="text-right">{risk.AnswerScore}</td>
                            </tr>
                          ))}
                      </LoadingOrEmpty>
                    </tbody>
                  </Table>
                </Col>
                {riskSummary && account && (
                  <Col lg="3">
                    <ProfileRiskSummary account={account} riskSummary={riskSummary} isRiskByPerson={isRiskByPerson}/>
                  </Col>
                )}
              </Row>
              {riskSummary && riskSummary.ProfileRisk.length != 0 && (
                <Row>
                  <Col sm="9">
                    <div className="pt-3 pl-2 pr-2">
                      <h6>
                        {riskSummary.ProfileRiskValue}
                        <span className="float-right d-block text-primary ">
                          Total {getTotalScore(riskSummary)} points
                        </span>
                      </h6>
                      <Progress
                        className={style.progress}
                        value={riskSummary.ProfileRiskPercentage}
                        max="100"
                      />
                    </div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Fragment>
      )}
    </Col>
  </Row>
)}

const RiskSummary = ({ account, riskSummary }) => (
  <div className={style.riskSummary}>
    <p className="h5 mb-2">Summary</p>
    <p>Check every 1 day</p>
    <p>Last check Yesterday</p>
    <p>
      <Badge color="secondary">{riskSummary.BOLevel}</Badge> Level to BO
    </p>
    {account.AccountWayOfAction != "" && (
      <p>
        <Badge color="secondary">{account.AccountWayOfAction} structure</Badge>
      </p>
    )}
    <p>
      Risk{" "}
      <Badge color={GetRiskClassName(account.AccountRisk)} className={style.badge}>
        {account.AccountRisk}
      </Badge>
    </p>
  </div>
);

const ProfileRiskSummary = ({ account, riskSummary, isRiskByPerson }) => (
  <div className={style.profileRiskSummary}>
    <p className="h5 mb-2">Summary</p>
    <span className={style.riskWarning} id="warning-risk">
      {!isRiskByPerson && (
        <Fragment>
          <i className="fa fa-exclamation-triangle mr-2" aria-hidden="true" />
          Profile mismatch warning
        </Fragment>
      )}
    </span>
    <table>
      <thead />
      <tbody>
        <tr>
          <td>Profile risk</td>
          <td>
            <FormattedNumber value={account.AccountProfileRiskPercentage} dataTypeDecimalName="percentage" />%
          </td>
          <td>
            <Badge color={GetRiskClassName(account.AccountProfileRisk)} className={style.badge}>
              {account.AccountProfileRisk}
            </Badge>
          </td>
        </tr>
        <tr>
          <td>Current risk</td>
          <td>
            <FormattedNumber
              value={account.AccountCurrentProfileRiskPercentage}
              dataTypeDecimalName="percentage"
            />
            %
          </td>
          <td>
            <Badge color={GetRiskClassName(account.AccountCurrentProfileRisk)} className={style.badge}>
              {account.AccountCurrentProfileRisk}
            </Badge>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const LoadingOrEmpty = ({ isLoading, isEmpty, children }) =>
  isLoading ? (
    <tr>
      <td colSpan="5" className="pl-2 pt-3 pb-3">
        <LoadingMessage className={style.loading} text="Retrieving risk info" />
      </td>
    </tr>
  ) : isEmpty ? (
    <tr>
      <td colSpan="5" className="pl-2 pt-3 pb-3">
        <h5 className={style.loading} style={{ textAlign: "center" }}>
          No risk info registerd
        </h5>{" "}
      </td>
    </tr>
  ) : (
    children
  );

const getTotalScore = (riskSummary) =>
  riskSummary && riskSummary.ProfileRisk.reduce((sum, e) => (sum += parseInt(e.AnswerScore, 10)), 0);

const getTotalRiskScore = (riskSummary, decimalPlaces) =>
  riskSummary && riskSummary.Risk.reduce((sum, e) => (sum += parseFloat(e.Score, 10)), 0).toFixed(decimalPlaces);

export default AccountRiskTable;
